import React, { useState } from 'react';
import Button from '../Button/Button';
import './UploadDocumentModal.css';

interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File, type: string, commentaire?: string) => Promise<void>;
  preselectedType?: string;
}

// Types de documents modifiables pour les prestataires
const DOCUMENT_TYPES = [
  "Déclaration sur l'honneur",
  'Extrait K-Bis',
  "Attestations d'assurance",
  'Garantie décennale',
  'Attestation fiscale et sociale',
  'Agréments, certifications, habilitations',
  "CV de l'équipe encadrante",
  'RIB'
];

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({ 
  isOpen, 
  onClose, 
  onUpload, 
  preselectedType 
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedType, setSelectedType] = useState<string>(preselectedType || '');
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Mettre à jour le type sélectionné quand preselectedType change
  React.useEffect(() => {
    if (preselectedType) {
      setSelectedType(preselectedType);
    }
  }, [preselectedType]);

  // Réinitialiser le formulaire
  const resetForm = () => {
    setSelectedFile(null);
    setSelectedType(preselectedType || '');
    setError(null);
  };

  // Gérer la fermeture du modal
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Gérer la sélection du fichier
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Vérifier le type de fichier
      if (file.type !== 'application/pdf') {
        setError('Seuls les fichiers PDF sont acceptés');
        setSelectedFile(null);
        return;
      }
      
      // Vérifier la taille (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        setError('Le fichier est trop volumineux (max 10 Mo)');
        setSelectedFile(null);
        return;
      }

      setError(null);
      setSelectedFile(file);
    }
  };

  // Gérer la soumission du formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setError('Veuillez sélectionner un fichier');
      return;
    }

    if (!selectedType) {
      setError('Veuillez sélectionner un type de document');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      await onUpload(selectedFile, selectedType);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="upload-document-modal-overlay" onClick={handleClose}>
      <div className="upload-document-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="upload-document-modal-header">
          <h2>Ajouter un document</h2>
          <button className="upload-document-modal-close" onClick={handleClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="upload-document-form">
          {/* Sélection du type */}
          {/* <div className="form-group">
            <label htmlFor="documentType">Type de document *</label>
            <select
              id="documentType"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              disabled={!!preselectedType || uploading}
              required
            >
              <option value="">-- Sélectionner un type --</option>
              {DOCUMENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div> */}

          {/* Sélection du fichier */}
          <div className="form-group">
            <label htmlFor="documentFile">Fichier PDF *</label>
            <input
              id="documentFile"
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
              disabled={uploading}
              required
            />
            {selectedFile && (
              <p className="file-info">
                Fichier sélectionné : {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} Mo)
              </p>
            )}
          </div>

          {/* Message d'erreur */}
          {error && <div className="upload-error-message">{error}</div>}

          {/* Boutons d'action */}
          <div className="upload-document-modal-actions">
            <Button
              type="button"
              onClick={handleClose}
              value="Annuler"
              variant="secondary"
              disabled={uploading}
            />
            <Button
              type="submit"
              value={uploading ? 'Envoi en cours...' : 'Ajouter le document'}
              disabled={uploading || !selectedFile || !selectedType}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadDocumentModal;
